import {
  createController,
  currentMonthFirst,
  emptyOptionSearch,
  messageFromError,
  optionQueryAction,
  parseMonth,
  todayIso,
} from '@eo/shared';

import {
  createClient,
  createHoliday,
  createStaff,
  fetchCfoTimeEntries,
  fetchClients,
  fetchAttribution,
  fetchHolidays,
  fetchOverview,
  fetchStaff,
  fetchTaskTypes,
  syncTaskTypes,
  removeHoliday,
  updateClient,
  updateStaff,
  verifyIntegrations,
} from './api/api';
import type {
  AdminCfoDay,
  ClientItem,
  CreateClientBody,
  CreateStaffBody,
  VerifyResult,
  StaffItem,
  UpdateClientBody,
  UpdateStaffBody,
} from './api/types';
import type { HolidayItem } from './api/types';
import {
  listParamsFor,
  parseRegistryView,
  parseTab,
  parseYear,
  REFERENCE_LIST_PARAMS,
} from './helpers';
import type { AdminState, ClientOwnerChange } from './types';

const initialState: AdminState = {
  date: todayIso(),
  overview: [],
  staff: [],
  clients: [],
  taskTypes: [],
  timeEntries: [],
  probes: {},
  staffPagination: null,
  clientsPagination: null,
  taskTypesPagination: null,
  year: new Date().getUTCFullYear(),
  clientBudgets: [],
  supplements: [],
  assignments: [],
  splits: [],
  timeOff: [],
  holidays: [],
  month: currentMonthFirst(),
  attribution: null,
  options: { clients: emptyOptionSearch(), users: emptyOptionSearch() },
};

function replaceById<T extends { id: string }>(rows: T[], next: T): T[] {
  return rows.map((row) => (row.id === next.id ? next : row));
}

export const adminController = createController<AdminState>({
  name: 'admin',
  state: initialState,
  fetchData: async ({ queryParams }) => {
    const params = (queryParams ?? {}) as Record<string, string | undefined>;
    const date = params.date && /^\d{4}-\d{2}-\d{2}$/.test(params.date) ? params.date : todayIso();
    const tab = parseTab(params.tab ?? null);
    const registryView = parseRegistryView(params.view ?? null);
    const year = parseYear(params.year ?? null);

    const [overview, staff, clients, taskTypes] = await Promise.all([
      fetchOverview(date),
      fetchStaff(listParamsFor('staff', tab, registryView, params)),
      fetchClients(listParamsFor('clients', tab, registryView, params)),
      fetchTaskTypes(listParamsFor('task-types', tab, registryView, params)),
    ]);

    // The per-CFO fan-out hits ClickUp once per member, so it only runs when
    // the Time Tracking tab is actually in the URL — and narrows to one request
    // when the tab is showing a single member.
    const cfo = params.cfo || undefined;
    let timeEntries: AdminCfoDay[] = [];
    if (tab === 'time-tracking') {
      timeEntries = await Promise.all(
        staff.staff
          .filter((member) => member.isActive && (!cfo || member.email === cfo))
          .map(async (member) => {
            const base = {
              userId: member.id,
              email: member.email,
              name: member.name,
            };
            try {
              const day = await fetchCfoTimeEntries(date, member.email);
              return { ...base, entries: day.entries, totalMinutes: day.totalMinutes, error: null };
            } catch (err) {
              return {
                ...base,
                entries: [],
                totalMinutes: 0,
                error: messageFromError(err, 'Could not load entries.'),
              };
            }
          }),
      );
    }

    // The holiday calendar is the only capacity config Admin still owns; the
    // rest follows its client or its member. It is year-scoped, so it loads
    // only when its registry view is in the URL.
    let holidays: HolidayItem[] = [];
    if (tab === 'registries' && registryView === 'holidays') {
      holidays = (await fetchHolidays(year)).holidays;
    }

    // Attribution reads the month's synced ClickUp hours, so it too loads only
    // when its registry view is on screen.
    const month = parseMonth(params.month);
    const attribution =
      tab === 'registries' && registryView === 'attribution'
        ? await fetchAttribution(month, listParamsFor('attribution', tab, registryView, params))
        : null;

    return {
      date,
      year,
      holidays,
      month,
      attribution,
      overview: overview.cfos,
      staff: staff.staff,
      clients: clients.clients,
      taskTypes: taskTypes.taskTypes,
      timeEntries,
      probes: {},
      staffPagination: staff.pagination,
      clientsPagination: clients.pagination,
      taskTypesPagination: taskTypes.pagination,
    };
  },
  asyncActions: {
    queryOptions: optionQueryAction<AdminState>({
      clients: async ({ search, page }) => {
        const { clients, pagination } = await fetchClients({ search, page, active: true });
        return { items: clients, pagination };
      },
      users: async ({ search, page }) => {
        const { staff, pagination } = await fetchStaff({ search, page, active: true });
        return { items: staff, pagination };
      },
    }),
    // Probe results live beside the rows rather than in them: the overview
    // payload is stored state, and a verify must not look like a fresh load.
    verifyIntegrations: {
      action: async (payload) => {
        const { userId } = payload as { userId?: string };
        return verifyIntegrations(userId);
      },
      handlers: {
        onSuccess: (state, { action }) => {
          const { results } = (action as { payload: { results: VerifyResult[] } }).payload;
          return {
            ...state,
            probes: results.reduce((acc, row) => ({ ...acc, [row.userId]: row }), {
              ...state.probes,
            }),
          };
        },
      },
    },
    createStaff: {
      action: async (payload) => createStaff(payload as CreateStaffBody),
      handlers: {
        onSuccess: (state, { action }) => ({
          ...state,
          staff: [...state.staff, (action as { payload: { staff: StaffItem } }).payload.staff],
        }),
      },
    },
    // Client ownership is not a user field, so a save is the user PATCH plus one
    // PATCH per client that moved; the client list is refetched afterwards
    // because those rows changed underneath whatever else is showing them.
    updateStaff: {
      action: async (payload) => {
        const { id, patch, clientChanges } = payload as {
          id: string;
          patch: UpdateStaffBody;
          clientChanges?: ClientOwnerChange[];
        };
        const updated = await updateStaff(id, patch);
        const changes = clientChanges ?? [];
        for (const change of changes) {
          await updateClient(change.clientId, { ownerUserId: change.ownerUserId });
        }
        if (changes.length === 0) return updated;
        const refreshed = await fetchClients(REFERENCE_LIST_PARAMS);
        return { ...updated, clients: refreshed.clients };
      },
      handlers: {
        onSuccess: (state, { action }) => {
          const payload = (action as { payload: { staff: StaffItem; clients?: ClientItem[] } })
            .payload;
          return {
            ...state,
            staff: replaceById(state.staff, payload.staff),
            clients: payload.clients ?? state.clients,
          };
        },
      },
    },
    // The sync rewrites rows the grid is showing, so the list is read back
    // rather than patched.
    syncTaskTypes: {
      action: async () => {
        await syncTaskTypes();
        return fetchTaskTypes(REFERENCE_LIST_PARAMS);
      },
      handlers: {
        onSuccess: (state, { action }) => {
          const { taskTypes, pagination } = (
            action as {
              payload: {
                taskTypes: AdminState['taskTypes'];
                pagination: AdminState['taskTypesPagination'];
              };
            }
          ).payload;
          return { ...state, taskTypes, taskTypesPagination: pagination };
        },
      },
    },
    createClient: {
      action: async (payload) => createClient(payload as CreateClientBody),
      handlers: {
        onSuccess: (state, { action }) => ({
          ...state,
          clients: [
            ...state.clients,
            (action as { payload: { client: ClientItem } }).payload.client,
          ],
        }),
      },
    },
    updateClient: {
      action: async (payload) => {
        const { id, patch } = payload as { id: string; patch: UpdateClientBody };
        return updateClient(id, patch);
      },
      handlers: {
        onSuccess: (state, { action }) => ({
          ...state,
          clients: replaceById(
            state.clients,
            (action as { payload: { client: ClientItem } }).payload.client,
          ),
        }),
      },
    },
    createHoliday: {
      action: async (payload) =>
        createHoliday(payload as { country: string; date: string; name: string }),
      handlers: {
        onSuccess: (state, { action }) => ({
          ...state,
          holidays: [
            ...state.holidays,
            (action as { payload: { holiday: HolidayItem } }).payload.holiday,
          ],
        }),
      },
    },
    removeHoliday: {
      action: async (payload) => {
        const { id } = payload as { id: string };
        await removeHoliday(id);
        return { id };
      },
      handlers: {
        onSuccess: (state, { action }) => ({
          ...state,
          holidays: state.holidays.filter(
            (row) => row.id !== (action as { payload: { id: string } }).payload.id,
          ),
        }),
      },
    },
  },
});
